<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePipelinesStore } from 'dashboard/stores/pipelines';

const props = defineProps({
  deal: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['submit', 'close']);

const { t } = useI18n();
const pipelinesStore = usePipelinesStore();

const pipelines = computed(() => pipelinesStore.getRecords);

const defaultForm = () => ({
  name: '',
  amount: null,
  expectedCloseDate: '',
  pipelineId: '',
  stageId: '',
  contactId: '',
  assigneeId: '',
});

const form = reactive(defaultForm());

const stagesForSelectedPipeline = computed(() => {
  if (!form.pipelineId) return [];
  const pipeline = pipelines.value.find(p => p.id === Number(form.pipelineId));
  return pipeline?.stages || [];
});

const isEditing = computed(() => Boolean(props.deal?.id));

const formTitle = computed(() =>
  isEditing.value ? t('DEALS.EDIT.TITLE') : t('DEALS.CREATE.TITLE')
);

const errors = reactive({});

const validateForm = () => {
  errors.name = !form.name.trim()
    ? t('DEALS.FORM.VALIDATION.NAME_REQUIRED')
    : '';
  errors.contactId = !form.contactId
    ? t('DEALS.FORM.VALIDATION.CONTACT_REQUIRED')
    : '';
  errors.pipelineId = !form.pipelineId
    ? t('DEALS.FORM.VALIDATION.PIPELINE_REQUIRED')
    : '';
  errors.stageId = !form.stageId
    ? t('DEALS.FORM.VALIDATION.STAGE_REQUIRED')
    : '';

  return (
    !errors.name && !errors.contactId && !errors.pipelineId && !errors.stageId
  );
};

const isFormInvalid = computed(
  () =>
    !form.name.trim() || !form.contactId || !form.pipelineId || !form.stageId
);

const populateForm = () => {
  if (props.deal) {
    Object.assign(form, {
      name: props.deal.name || '',
      amount: props.deal.amount ?? null,
      expectedCloseDate: props.deal.expectedCloseDate || '',
      pipelineId: props.deal.pipelineId || '',
      stageId: props.deal.stageId || '',
      contactId: props.deal.contactId || '',
      assigneeId: props.deal.assigneeId || '',
    });
  } else {
    Object.assign(form, defaultForm());
  }
  // Clear errors on population
  Object.keys(errors).forEach(key => {
    errors[key] = '';
  });
};

const handlePipelineChange = () => {
  form.stageId = '';
};

const handleSubmit = () => {
  if (!validateForm()) return;

  const payload = {
    name: form.name.trim(),
    amount: form.amount ? Number(form.amount) : null,
    expectedCloseDate: form.expectedCloseDate || null,
    pipelineId: Number(form.pipelineId),
    stageId: Number(form.stageId),
    contactId: Number(form.contactId),
    assigneeId: form.assigneeId ? Number(form.assigneeId) : null,
  };

  emit('submit', payload);
};

const handleClose = () => emit('close');

watch(
  () => props.deal,
  () => populateForm(),
  { immediate: true }
);
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center">
    <div class="absolute inset-0 bg-black/40" @click="handleClose" />

    <div
      class="relative z-10 w-full max-w-lg max-h-[90vh] overflow-y-auto bg-white dark:bg-slate-800 rounded-lg shadow-xl mx-4"
    >
      <div
        class="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-700"
      >
        <h3 class="text-base font-medium text-slate-900 dark:text-slate-100">
          {{ formTitle }}
        </h3>
        <button
          class="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
          @click="handleClose"
        >
          <i class="i-lucide-x size-5" />
        </button>
      </div>

      <form
        class="flex flex-col gap-4 px-6 py-4"
        @submit.prevent="handleSubmit"
      >
        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-slate-700 dark:text-slate-300">
            {{ t('DEALS.FORM.FIELDS.NAME') }}
            <span class="text-red-500">*</span>
          </label>
          <input
            v-model="form.name"
            type="text"
            class="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500"
            :placeholder="t('DEALS.FORM.PLACEHOLDERS.NAME')"
          />
          <span v-if="errors.name" class="text-xs text-red-500">
            {{ errors.name }}
          </span>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col gap-1.5">
            <label
              class="text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ t('DEALS.FORM.FIELDS.AMOUNT') }}
            </label>
            <input
              v-model="form.amount"
              type="number"
              min="0"
              step="0.01"
              class="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500"
              :placeholder="t('DEALS.FORM.PLACEHOLDERS.AMOUNT')"
            />
          </div>

          <div class="flex flex-col gap-1.5">
            <label
              class="text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ t('DEALS.FORM.FIELDS.EXPECTED_CLOSE_DATE') }}
            </label>
            <input
              v-model="form.expectedCloseDate"
              type="date"
              class="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500"
            />
          </div>
        </div>

        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-slate-700 dark:text-slate-300">
            {{ t('DEALS.FORM.FIELDS.PIPELINE') }}
            <span class="text-red-500">*</span>
          </label>
          <select
            v-model="form.pipelineId"
            class="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500"
            @change="handlePipelineChange"
          >
            <option value="" disabled>
              {{ t('DEALS.FORM.PLACEHOLDERS.PIPELINE') }}
            </option>
            <option v-for="p in pipelines" :key="p.id" :value="p.id">
              {{ p.name }}
            </option>
          </select>
          <span v-if="errors.pipelineId" class="text-xs text-red-500">
            {{ errors.pipelineId }}
          </span>
        </div>

        <div class="flex flex-col gap-1.5">
          <label class="text-sm font-medium text-slate-700 dark:text-slate-300">
            {{ t('DEALS.FORM.FIELDS.STAGE') }}
            <span class="text-red-500">*</span>
          </label>
          <select
            v-model="form.stageId"
            :disabled="!form.pipelineId"
            class="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <option value="" disabled>
              {{ t('DEALS.FORM.PLACEHOLDERS.STAGE') }}
            </option>
            <option
              v-for="s in stagesForSelectedPipeline"
              :key="s.id"
              :value="s.id"
            >
              {{ s.name }}
            </option>
          </select>
          <span v-if="errors.stageId" class="text-xs text-red-500">
            {{ errors.stageId }}
          </span>
        </div>

        <div class="grid grid-cols-2 gap-4">
          <div class="flex flex-col gap-1.5">
            <label
              class="text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ t('DEALS.FORM.FIELDS.CONTACT') }}
              <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.contactId"
              type="number"
              class="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500"
              :placeholder="t('DEALS.FORM.PLACEHOLDERS.CONTACT')"
            />
            <span v-if="errors.contactId" class="text-xs text-red-500">
              {{ errors.contactId }}
            </span>
          </div>

          <div class="flex flex-col gap-1.5">
            <label
              class="text-sm font-medium text-slate-700 dark:text-slate-300"
            >
              {{ t('DEALS.FORM.FIELDS.ASSIGNEE') }}
            </label>
            <input
              v-model="form.assigneeId"
              type="number"
              class="h-9 px-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-900 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500"
              :placeholder="t('DEALS.FORM.PLACEHOLDERS.ASSIGNEE')"
            />
          </div>
        </div>

        <div class="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-slate-100 transition-colors"
            @click="handleClose"
          >
            {{ t('DEALS.FORM.CANCEL') }}
          </button>
          <button
            type="submit"
            :disabled="isFormInvalid"
            class="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {{ t('DEALS.FORM.SAVE') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
